import { createTheme } from '@mui/material'
import { Theme } from '@mui/material/styles'
import i18n from 'i18n'
import { CSSProperties } from 'react'
import { APP_COLOR } from 'utils/colors'
import { is24meApp, isGroupcalApp } from 'utils/appType'
import { isPreviewMode } from 'utils/user'
import { ResolvedThemeMode } from 'components/providers/AppSettingsProvider'

interface PaletteOverride {
  surface: {
    canvas: CSSProperties['color']
    app: CSSProperties['color']
    card: CSSProperties['color']
    subtle: CSSProperties['color']
    hover: CSSProperties['color']
    active: CSSProperties['color']
    greyButton: CSSProperties['color']
  }
  calendar: {
    grid: CSSProperties['color']
    todayBg: CSSProperties['color']
    todayText: CSSProperties['color']
    headerCurrentBg: CSSProperties['color']
  }
  card: {
    primary: CSSProperties['color']
  }
  button: {
    background: {
      primary: CSSProperties['color']
      selected: CSSProperties['color']
    }
    action: {
      main: CSSProperties['color']
      hover: CSSProperties['color']
      active: CSSProperties['color']
      disabled: CSSProperties['color']
      contrastText: CSSProperties['color']
    }
    text: {
      primary: CSSProperties['color']
      approve: CSSProperties['color']
      tenative: CSSProperties['color']
      negative: CSSProperties['color']
      groupcal: CSSProperties['color']
      link: CSSProperties['color']
      darkRed: CSSProperties['color']
    }
  }
  input: {
    border: {
      primary: CSSProperties['color']
    }
    placeholder: CSSProperties['color']
  }
  border: {
    primary: CSSProperties['color']
  }
}

declare module '@mui/material/styles' {
  interface Palette extends PaletteOverride {}
  interface PaletteOptions extends PaletteOverride {}
}

export function createAppTheme(mode: ResolvedThemeMode = 'light') {
  const isDark = mode === 'dark'
  const is24me = is24meApp()
  const accentColor = isDark
    ? isGroupcalApp()
      ? '#ff4f71'
      : APP_COLOR
    : APP_COLOR
  const actionButtonColors = is24me
    ? {
        main: '#F2C700',
        hover: '#DFB800',
        active: '#CBA700',
        disabled: '#F2E4A3',
        contrastText: '#FFFFFF'
      }
    : {
        main: accentColor,
        hover: '#BE3039',
        active: '#8F242B',
        disabled: '#FAC5C8',
        contrastText: '#FFFFFF'
      }

  return createTheme({
    direction: i18n.dir(),
    typography: {
      fontFamily: ['"NotoSans"', '"Noto Sans Hebrew"', 'sans-serif'].join(','),
      h1: {
        fontFamily: '"NotoSans", "Noto Sans Hebrew", sans-serif',
        fontWeight: 600
      },
      h2: {
        fontFamily: '"NotoSans", "Noto Sans Hebrew", sans-serif',
        fontWeight: 600
      },
      h3: {
        fontFamily: '"NotoSans", "Noto Sans Hebrew", sans-serif',
        fontWeight: 600
      },
      h4: {
        fontFamily: '"NotoSans", "Noto Sans Hebrew", sans-serif',
        fontWeight: 600
      },
      h5: {
        fontFamily: '"NotoSans", "Noto Sans Hebrew", sans-serif',
        fontWeight: 600
      },
      h6: {
        fontFamily: '"NotoSans", "Noto Sans Hebrew", sans-serif',
        fontWeight: 600
      },
      body1: {
        fontFamily: '"NotoSans", "Noto Sans Hebrew", sans-serif'
      },
      body2: {
        fontFamily: '"NotoSans", "Noto Sans Hebrew", sans-serif'
      },
      button: {
        fontFamily: '"NotoSans", "Noto Sans Hebrew", sans-serif',
        fontWeight: 500
      }
    },
    palette: {
      mode,
      surface: {
        canvas: isDark ? '#181c24' : '#f7f8fb',
        app: isDark ? '#1b2029' : '#F9F9F9',
        card: isDark ? '#242b36' : '#FFFFFF',
        subtle: isDark ? '#2a323f' : '#f5f5f5',
        hover: isDark ? '#3a4556' : '#f6f6f6',
        active: isDark ? '#364152' : '#e9e9e9',
        greyButton: isDark ? '#4A5770' : '#dddddd'
      },
      calendar: {
        grid: isDark ? '#3b4656' : '#E4E4E4',
        todayBg: isDark ? '#23344d' : '#f5f5f5',
        todayText: isDark ? '#9dc8ff' : '#0F7FEB',
        headerCurrentBg: isDark ? '#23344d' : '#f5f5f5'
      },
      border: {
        primary: isDark ? '#364152' : '#E4E4E4'
      },
      background: {
        default: isDark ? '#1b2029' : '#F9F9F9',
        paper: isDark ? '#242b36' : '#eeeeee'
      },
      divider: isDark ? '#3b4656' : '#F2F2F2',
      text: {
        primary: isDark ? '#edf1f7' : '#3D3B3B',
        secondary: isDark ? '#bcc5d3' : '#848484'
      },
      card: {
        primary: isDark ? '#242b36' : '#FFFFFF'
      },
      button: {
        background: {
          primary: isDark ? '#303947' : '#FFFFFF',
          selected: isDark ? '#364152' : '#f6f6f6'
        },
        action: actionButtonColors,
        text: {
          groupcal: accentColor,
          primary: isDark ? '#edf1f7' : '#3D3B3B',
          approve: isDark ? '#00ff5d' : '#006400',
          tenative: isDark ? '#ffab47' : '#e2ab4c',
          negative: isDark ? '#ff7777' : 'red',
          link: isDark ? '#81b5ff' : '#0F7FEB',
          darkRed: '#EA5455'
        }
      },
      input: {
        border: {
          primary: isDark ? '#3a4453' : '#F4F4F6'
        },
        placeholder: isDark ? 'rgb(154 166 184)' : 'rgb(129 129 129)'
      },
      grey: {
        500: isDark ? '#536072' : '#DEE4E6',
        100: isDark ? '#2a323f' : '#EFEFEF'
      },
      error: {
        main: '#EB5545'
      },
      success: {
        main: '#00CA73'
      },
      primary: {
        main: accentColor
      }
    },
    spacing: (factor: number) => `${0.25 * factor}rem`,
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none'
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: '8px'
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? '#242b36' : '#FFFFFF',
            backgroundImage: 'none'
          }
        }
      }
    }
  })
}

function getQueryResolvedThemeMode(): ResolvedThemeMode | undefined {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search)

    if (isPreviewMode()) {
      const themeMode = urlParams.get('themeMode')

      if (themeMode === 'dark' || themeMode === 'light') {
        return themeMode
      }

      if (themeMode === 'system' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
      }
    }
  }

  return undefined
}

export function getResolvedThemeModeFromDom(): ResolvedThemeMode {
  const queryResolvedTheme = getQueryResolvedThemeMode()

  if (queryResolvedTheme) {
    return queryResolvedTheme
  }

  if (typeof document !== 'undefined') {
    const dataTheme = document.documentElement.getAttribute('data-theme')
    if (dataTheme === 'dark' || dataTheme === 'light') {
      return dataTheme
    }
  }

  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light'
  }

  return 'light'
}

let cachedResolvedThemeMode: ResolvedThemeMode | undefined
let cachedTheme: Theme | undefined

function getRuntimeTheme(): Theme {
  const resolvedMode = getResolvedThemeModeFromDom()

  if (!cachedTheme || cachedResolvedThemeMode !== resolvedMode) {
    cachedTheme = createAppTheme(resolvedMode)
    cachedResolvedThemeMode = resolvedMode
  }

  return cachedTheme
}

export const theme = new Proxy({} as Theme, {
  get(_target, prop: keyof Theme) {
    return getRuntimeTheme()[prop]
  }
}) as Theme
